/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import {
  Grid,
  Stack,
  Typography,
  TextField,
  Autocomplete,
  Checkbox,
  ListItemText,
  Alert,
} from "@mui/material";
import FormikForm from "components/layout/FormikForm";
import { Form, FormikProps } from "formik";
import useHttp from "hooks/http.hook";
import {
  BillingEncounterInterface,
  AccountingInterface,
  HeaderEnum,
  PricingInterface,
  AccountingMovementInterface,
} from "medicalsystem-shared";
import { useTranslation } from "react-i18next";
import Button from "components/generals/Button";
import PermissionWrapper from "components/generals/PermissionWrapper";
import BillingEncounterTable from "components/tables/AppointmentBillingEncounterTable";
import { CoreAPI_URLs } from "constants/http.const";
import { EntityPermissions, PermissionsUser } from "types";
import { AccountingStatusEnum } from "medicalsystem-shared";
import { getNewPatientAccountingValues } from "utils/model.utils";
import EventSlice, { MessageType, MessageState } from "store/slices/event.slice";

/**
 * Props for the BillingEncounterForm component.
 * @interface BillingEncounterFormProps
 * @property {string} appointmentId - The appointment ID to create billing encounters for
 * @property {string} patientId - The patient ID associated with the billing
 * @property {() => void} onCloseAction - Callback when form is closed
 * @property {boolean} [insurance] - Flag indicating if billing is for insurance
 */
interface BillingEncounterFormProps {
  appointmentId: string;
  patientId: string;
  onCloseAction: () => void;
  insurance?:boolean;
  nonBillable?:boolean;
}

interface SelectedPricingItem {
  id: string;
  name: string;
  description: string;
  price: number;
  discountType?: string;
  discountValue?: number;
  uniqueId: string;
  cpt:string;
  isClaim:boolean;
  coInsuranceApplicable?: boolean;
}

const accountingCreationCache = new Map<
  string,
  { promise: Promise<AccountingInterface>; timestamp: number }
>();

const CACHE_EXPIRY = 30000;
const MAX_RELOAD_ATTEMPTS = 3;
const RELOAD_DELAY = 2000;

const BillingEncounterForm: React.FC<BillingEncounterFormProps> = ({
  appointmentId,
  patientId,
  onCloseAction,
  insurance,
  nonBillable
}) => {
  const { t } = useTranslation();
  const [isHttpLoading, httpError, sendRequest] = useHttp();
  const dispatch = useDispatch();
  const { addErrorMessage, addSuccessMessage, addLoadingMessage, resetLoadingMessages } =
    EventSlice.actions;

  const [pricingOptions, setPricingOptions] = useState<PricingInterface[]>([]);
  const [selectedPricing, setSelectedPricing] = useState<SelectedPricingItem[]>([]);
  const [accounting, setAccounting] = useState<AccountingInterface | null>(null);
  const [tempAccountig, settempAccountig] = useState<boolean>(false);
  const [initialTotalAmount, setInitialTotalAmount] = useState<number>(0);
  const [initialAmountDue, setInitialAmountDue] = useState<number>(0);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [amountDue, setAmountDue] = useState<number>(0);
  const [isInitializing, setIsInitializing] = useState<boolean>(true);
  const [isDataReady, setIsDataReady] = useState<boolean>(false);
  const [componentError, setComponentError] = useState<string | null>(null);
  const [reloadAttempts, setReloadAttempts] = useState<number>(0);
  const [isAutoReloading, setIsAutoReloading] = useState<boolean>(false);
  const [initializationKey, setInitializationKey] = useState<number>(0);
  const [accumulatedTotal, setAccumulatedTotal] = useState<number>(0);

  const isSubmitting = useRef(false);
  const reloadTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isLoading = isInitializing || !isDataReady || isAutoReloading;

  useEffect(() => {
    let total = 0;
    selectedPricing.forEach((service) => {
      const discount =
        service.discountType === "Percentage"
          ? (Number(service.discountValue) / 100) * Number(service.price)
          : Number(service.discountValue) || 0;
      total += Number(service.price) - discount;
    });
    setAccumulatedTotal(total);
  }, [selectedPricing]);

  const handleError = (
    message: string,
    error?: any,
    messageType: MessageType = MessageType.ERROR
  ) => {
    const messagePayload: MessageState = {
      message,
      messageType,
      showError: true,
      code: error?.response?.status?.toString() || error?.code || undefined,
      id: crypto.randomUUID(),
    };
    dispatch(addErrorMessage(messagePayload));
    setComponentError(message);
  };

  const handleSuccess = (message: string) => {
    const messagePayload: MessageState = {
      message,
      messageType: MessageType.SUCCESS,
      id: crypto.randomUUID(),
    };
    dispatch(addSuccessMessage(messagePayload));
  };

  const handleLoading = (message: string) => {
    const messagePayload: MessageState = {
      message,
      messageType: MessageType.LOADING,
      id: crypto.randomUUID(),
    };
    dispatch(addLoadingMessage(messagePayload));
  };

  const generateUniqueId = () => {
    return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  const isPricingSelected = (pricingId: string) => {
    return selectedPricing.some((item) => item.id === pricingId);
  };

  const handlePricingToggle = (pricing: PricingInterface) => {
    const newItem: SelectedPricingItem = {
      id: pricing.id,
      name: pricing.name,
      description: pricing.description,
      price: pricing.price,
      discountType: "No Discount",
      discountValue: 0,
      uniqueId: generateUniqueId(),
      cpt: pricing.cpt,
      isClaim: true,
      coInsuranceApplicable: false
    };
    setSelectedPricing((prev) => [...prev, newItem]);
  };

  const removePricingItem = (uniqueId: string) => {
    setSelectedPricing((prev) => prev.filter((item) => item.uniqueId !== uniqueId));
  };

  const cleanExpiredCache = () => {
    const now = Date.now();
    for (const [key, value] of accountingCreationCache.entries()) {
      if (now - value.timestamp > CACHE_EXPIRY) {
        accountingCreationCache.delete(key);
      }
    }
  };

  const resetFormState = () => {
    setAccounting(null);
    settempAccountig(false);
    setPricingOptions([]);
    setSelectedPricing([]);
    setInitialTotalAmount(0);
    setInitialAmountDue(0);
    setTotalAmount(0);
    setAmountDue(0);
    setIsInitializing(true);
    setIsDataReady(false);
    setComponentError(null);
    setIsAutoReloading(false);
  };

  const scheduleAutoReload = () => {
    if (reloadAttempts >= MAX_RELOAD_ATTEMPTS) {
      handleError(t("billingEncounterForm.errorMaxReloadAttemptsReached"));
      return;
    }
    setIsAutoReloading(true);
    handleLoading(t("billingEncounterForm.loadingAutoReloading"));
    reloadTimeoutRef.current = setTimeout(() => {
      setReloadAttempts((prev) => prev + 1);
      setInitializationKey((prev) => prev + 1);
    }, RELOAD_DELAY);
  };

  const fetchAccount = async (): Promise<AccountingInterface | null> => {
    try {
      const response = await sendRequest<{ results: AccountingInterface }>(
        CoreAPI_URLs.ACCOUNTING_BY_PATIENT_ID,
        (res) => res,
        "GET",
        null,
        [{ header: HeaderEnum.RESOURCE_ID, value: patientId }]
      );
      const accounting = response?.data?.results || response?.data;
      return accounting || null;
    } catch (error: any) {
      if (error?.response?.status === 404) {
        return null;
      }
      throw error;
    }
  };

  const createNewAccount = async (): Promise<AccountingInterface | null> => {
    try {
      const newAccountData = getNewPatientAccountingValues(patientId);
      const accountPayload = {
        ...newAccountData,
        status: AccountingStatusEnum.OPEN,
        totalAmount: 0,
        amountDue: 0,
      };
      const createResponse = await sendRequest<{ results: AccountingInterface[] }>(
        CoreAPI_URLs.ACCOUNTING_GENERAL_OPERATIONS,
        (res) => res,
        "POST",
        JSON.stringify(accountPayload),
        [{ header: HeaderEnum.RESOURCE_ID, value: patientId }],
        { successMessage: t("billingEncounterForm.successAccountCreated") }
      );
      const accountsArray = createResponse?.data?.results || createResponse?.data;
      if (accountsArray && accountsArray.length > 0) {
        const createdAccount = accountsArray[0];
        return createdAccount;
      }
      return null;
    } catch (error: any) {
      const isDuplicateError = error?.response?.status === 409;
      if (isDuplicateError) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        try {
          const existingAccount = await sendRequest<{ results: AccountingInterface }>(
            CoreAPI_URLs.ACCOUNTING_BY_PATIENT_ID,
            (res) => res,
            "GET",
            null,
            [{ header: HeaderEnum.RESOURCE_ID, value: patientId }]
          );
          const accounting = existingAccount?.data?.results || existingAccount?.data;
          if (accounting) {
            return accounting;
          }
        } catch (fetchError) {
          console.error("Failed to fetch existing accounting after duplicate error:", fetchError);
        }
      }
      throw error;
    }
  };

  const fetchOrCreateAccount = async (): Promise<{
    accounting: AccountingInterface | null;
    wasCreated: boolean;
  }> => {
    try {
      cleanExpiredCache();
      const cached = accountingCreationCache.get(patientId);
      if (cached && Date.now() - cached.timestamp < CACHE_EXPIRY) {
        try {
          const accounting = await cached.promise;
          return { accounting, wasCreated: false };
        } catch (error) {
          accountingCreationCache.delete(patientId);
        }
      }

      let accounting = await fetchAccount();
      if (accounting) {
        return { accounting, wasCreated: false };
      }

      const creationPromise = createNewAccount();
      accountingCreationCache.set(patientId, {
        promise: creationPromise as Promise<AccountingInterface>,
        timestamp: Date.now(),
      });

      try {
        const result = await creationPromise;
        return { accounting: result, wasCreated: true };
      } finally {
        accountingCreationCache.delete(patientId);
      }
    } catch (error) {
      accountingCreationCache.delete(patientId);
      throw error;
    }
  };

  const fetchPricing = async () => {
    try {
      const response = await sendRequest<{ results: PricingInterface[] }>(
        CoreAPI_URLs.PRICING_GET_ALL,
        (res) => res,
        "GET",
        null,
        [{ header: HeaderEnum.RESOURCE_ID, value: patientId }]
      );
      const pricings = response?.data?.results || [];
      setPricingOptions(pricings);
      return pricings;
    } catch (error) {
      console.error("Error fetching pricing:", error);
      setPricingOptions([]);
      throw error;
    }
  };

  const fetchLastAccountMovement = async (accountId: string) => {
    try {
      const url = `${CoreAPI_URLs.ACCOUNTING_MOVEMENT_GENERAL_OPERATIONS}/all`;
      const response = await sendRequest<{ results: AccountingMovementInterface[] }>(
        url,
        (res) => res,
        "GET",
        null,
        [{ header: HeaderEnum.PATIENT_ACCOUNT_NUMBER, value: accountId }]
      );
      const movements = response?.data?.results || response?.data || [];
      if (movements && movements.length > 0) {
        const lastMovement = movements[0];
        const totalAmount = lastMovement.totalAmount;
        const amountDue = lastMovement.amountDue;
        setInitialTotalAmount(totalAmount);
        setInitialAmountDue(amountDue);
        setTotalAmount(totalAmount);
        setAmountDue(amountDue);
        return { totalAmount, amountDue };
      } else {
        setInitialTotalAmount(0);
        setInitialAmountDue(0);
        setTotalAmount(0);
        setAmountDue(0);
        return { totalAmount: 0, amountDue: 0 };
      }
    } catch (error: any) {
      setInitialTotalAmount(0);
      setInitialAmountDue(0);
      setTotalAmount(0);
      setAmountDue(0);
      throw error;
    }
  };

  const initialize = async () => {
    try {
      handleLoading(t("billingEncounterForm.loadingInitializingForm"));
      dispatch(resetLoadingMessages());
      setIsInitializing(true);
      setComponentError(null);
      setIsDataReady(false);
      setIsAutoReloading(false);

      const { accounting: accountData, wasCreated } = await fetchOrCreateAccount();
      if (!accountData) {
        throw new Error(t("billingEncounterForm.failedfetch"));
      }

      setAccounting(accountData);
      settempAccountig(wasCreated);

      const [movementData, pricingData] = await Promise.all([
        fetchLastAccountMovement(accountData.id),
        fetchPricing(),
      ]);

      setIsDataReady(true);
      setIsInitializing(false);
      setIsAutoReloading(false);
      setReloadAttempts(0);
      dispatch(resetLoadingMessages());

      if (wasCreated) {
        handleSuccess(t("billingEncounterForm.successAccountCreatedAndFormRefreshed"));
        setTimeout(() => {
          setInitializationKey((prev) => prev + 1);
        }, 1000);
      }
    } catch (error: any) {
      setIsInitializing(false);
      setIsDataReady(false);
      setIsAutoReloading(false);
      const errorMessage = error?.message || t("billingEncounterForm.errorInitializingForm");
      handleError(errorMessage, error);
      dispatch(resetLoadingMessages());
      scheduleAutoReload();
    }
  };

  useEffect(() => {
    if (reloadTimeoutRef.current) {
      clearTimeout(reloadTimeoutRef.current);
      reloadTimeoutRef.current = null;
    }
    if (initializationKey > 0) {
      resetFormState();
    }
    initialize();

    return () => {
      accountingCreationCache.delete(patientId);
      dispatch(resetLoadingMessages());
      if (reloadTimeoutRef.current) {
        clearTimeout(reloadTimeoutRef.current);
        reloadTimeoutRef.current = null;
      }
    };
  }, [patientId, initializationKey]);

  useEffect(() => {
    if (httpError && !isSubmitting.current) {
      handleError((t("billingEncounterForm.requestErroHttp")), { message: httpError });
    }
  }, [httpError]);

  const handleCancel = async () => {
    if (reloadTimeoutRef.current) {
      clearTimeout(reloadTimeoutRef.current);
      reloadTimeoutRef.current = null;
    }
    if (tempAccountig && accounting) {
      try {
        handleLoading(t("billingEncounterForm.loadingDeletingTemporaryAccount"));
        await sendRequest(
          `${CoreAPI_URLs.ACCOUNTING_GENERAL_OPERATIONS}/${accounting.id}`,
          null,
          "DELETE",
          null,
          null
        );
        dispatch(resetLoadingMessages());
      } catch (error) {
        handleError(t("billingEncounterForm.errorDeletingTemporaryAccount"), error);
      }
    }
    onCloseAction();
  };

  const onSubmit = async () => {
    if (selectedPricing.length === 0) {
      handleCancel();
      return;
    }

    isSubmitting.current = true;
    try {
      handleLoading(t("billingEncounterForm.loadingCreatingBillingEncounter"));
      let newTotalAmount = initialTotalAmount;
      let newAmountDue = initialAmountDue;

      for (const pricing of selectedPricing) {
        const discount =
          pricing.discountType === "Percentage"
            ? (Number(pricing.discountValue) / 100) * Number(pricing.price)
            : Number(pricing.discountValue) || 0;
        const amount = Number(pricing.price) - discount;
        const encounterPayload = {
          appointment: { id: appointmentId },
          pricing: { id: pricing.id },
          accounting: { id: accounting!.id },
          price: amount,
          discount: pricing.discountValue || 0,
          pricingName: pricing.name?.trim() || "N/A",
          pricingDescription: pricing.description?.trim() || "N/A",
          isClaim: pricing.isClaim,
          applyCoInsurance: pricing.coInsuranceApplicable || false
        };

        const billingResponse = await sendRequest(
          CoreAPI_URLs.BILLING_ENCOUNTER_GENERAL_OPERATIONS,
          (response) => response,
          "POST",
          JSON.stringify(encounterPayload),
          null
        );

        const billingCreatedId = billingResponse?.data?.id || billingResponse?.data?.results?.id;
        if (!billingCreatedId) {
          handleError(
            `${t("billingEncounterForm.errorCreatingBillingEncounterFor")} ${pricing.name}`
          );
          continue;
        }

        newTotalAmount += amount;
        newAmountDue += amount;

        const accountMovementPayload = {
          accounting: { id: accounting!.id },
          date: new Date(),
          billingEncounter: { id: billingCreatedId },
          payment: null,
          amountDue: newAmountDue,
          totalAmount: newTotalAmount,
        };

        await sendRequest(
          CoreAPI_URLs.ACCOUNTING_MOVEMENT_GENERAL_OPERATIONS,
          (response) => response,
          "POST",
          JSON.stringify(accountMovementPayload)
        );

        setTotalAmount(newTotalAmount);
        setAmountDue(newAmountDue);
      }

      settempAccountig(false);
      dispatch(resetLoadingMessages());
      handleSuccess(t("billingEncounterForm.successBillingEncounterCreated"));
      onCloseAction();
    } catch (error: any) {
      handleError(t("billingEncounterForm.errorCreatingBillingEncounter"), error);
    } finally {
      isSubmitting.current = false;
      dispatch(resetLoadingMessages());
    }
  };

  const handleCheckboxClick = (
    option: PricingInterface,
    event: React.MouseEvent<HTMLButtonElement | HTMLInputElement>
  ) => {
    event.stopPropagation();
    if (!isPricingSelected(option.id)) {
      handlePricingToggle(option);
    }
  };
  if (isLoading) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <Typography>{t("billingEncounterForm.loadingInitializingForm")}</Typography>
      </div>
    );
  }

  if (componentError && !isAutoReloading) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {componentError}
        <Button onClick={() => setInitializationKey((prev) => prev + 1)} sx={{ ml: 2 }}>
          {t("common.retry")}
        </Button>
      </Alert>
    );
  }

  return (
    <FormikForm<Partial<BillingEncounterInterface>>
      initialValues={{
        pricing: { id: "" },
        price: 0,
        discount: 0,
        pricingName: "",
        pricingDescription: "",
      }}
      onSubmit={onSubmit}
    >
      {(formikProps: FormikProps<Partial<BillingEncounterInterface>>) => (
        <Form id="billingEncounterForm" autoComplete="off">
          <Stack
            direction="row"
            spacing={2}
            mb={2}
            sx={{ p: 1, bgcolor: "grey.50", borderRadius: 1 }}
          >
            <Typography variant="caption">
              {t("billingEncounterForm.patient")}:{" "}
              {accounting?.patient?.person?.name + " " + accounting?.patient?.person?.lastname ||
                "N/A"}
            </Typography>
            <Typography variant="caption">
              {t("billingEncounterForm.totalAmount")}: ${totalAmount.toFixed(2)}
            </Typography>
            <Typography variant="caption">
              {t("billingEncounterForm.amountDue")}: ${amountDue.toFixed(2)}
            </Typography>
            <Typography variant="caption">
              {t("billingEncounterForm.pricingOptions")}: {pricingOptions.length}
            </Typography>
          </Stack>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6} id="cy-pricingFormContainer">
              <Autocomplete
                id="cy-pricing"
                multiple
                options={pricingOptions}
                disableCloseOnSelect
                getOptionLabel={(option) => `${option.cpt} ${option.name}`}
                renderOption={(props, option, { selected }) => (
                  <li id="cy-pricing-item" {...props} onClick={() => handlePricingToggle(option)}>
                    <Checkbox
                      checked={isPricingSelected(option.id)}
                      style={{ marginRight: 8 }}
                      onClick={(event) => handleCheckboxClick(option, event)}
                    />
                    <ListItemText primary={
                      <div style={{ display: "flex", gap: "1rem" }}>
                        <div style={{ minWidth: 90 }}>{option.cpt}</div>
                        <div>{option.name}</div>
                      </div>}
                    />
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("billingEncounterForm.selectPricing")}
                    placeholder={t("billingEncounterForm.selectPricing")}
                  />
                )}
                value={[]}
                noOptionsText={t("billingEncounterForm.noOptions")}
              />
            </Grid>
            <Grid item xs={12}>
              <BillingEncounterTable
                selectedPricing={selectedPricing}
                setSelectedPricing={setSelectedPricing}
                onRemoveItem={removePricingItem}
                insurance={insurance}
                nonBillable={nonBillable}
              />
            </Grid>
            <Grid item xs={12} display="flex" alignItems="right">
              <TextField
                label={t("billingEncounterForm.accumulatedTotal")}
                value={`$${accumulatedTotal.toFixed(2)}`}
                InputProps={{ readOnly: true, sx: { color: "green" } }}
                sx={{
                  width: "300px",
                  ml: "auto",
                  "& .MuiInputLabel-root": { color: "green" },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "green" },
                    "&:hover fieldset": { borderColor: "darkgreen" },
                    "&.Mui-focused fieldset": { borderColor: "green" },
                  },
                }}
                fullWidth={false}
              />
            </Grid>
          </Grid>
          <Stack direction="row" spacing={2} mt={2}>
            <PermissionWrapper
              requiredPermissions={[PermissionsUser[EntityPermissions.BILLING_ENCOUNTER].CREATE]}
            >
              <Button
                id="cy-submit-billing-encounter"
                type="submit"
                variant="contained"
                color="primary"
                disabled={selectedPricing.length === 0 || isSubmitting.current}
              >
                {t("billingEncounterForm.submit")}
              </Button>
            </PermissionWrapper>
            <Button variant="outlined" onClick={handleCancel}>
              {t("common.cancel")}
            </Button>
          </Stack>
        </Form>
      )}
    </FormikForm>
  );
};

export default BillingEncounterForm;
